import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CalendarPlus, ClipboardList, Trees } from 'lucide-react';
import type { Event } from '@wht-xc-utils/shared/domain';

import { CoordinatorInput } from '../components/CoordinatorInput';
import { api } from '../lib/api';

export function EventsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [scoreSheetRows, setScoreSheetRows] = useState(20);
  const [coordinators, setCoordinators] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get<{ events: Event[] }>('/api/events'),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string; scoreSheetRows: number; coordinators: string[] }) =>
      api.post<{ event: Event }>('/api/events', input),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      navigate(`/events/${res.event.id}`);
    },
  });

  return (
    <>
      <section className="landing-hero">
        <div>
          <span className="eyebrow">The Horse Park at Woodside</span>
          <h1>Plan cross country with clarity.</h1>
          <p>
            Bring entries, volunteers, courses, judging posts, and packets into one calm operational
            workspace.
          </p>
        </div>
        <Trees aria-hidden="true" />
      </section>

      <div className="events-layout">
        <div className="card create-event-card">
          <span className="eyebrow">New workspace</span>
          <h2>Create Event</h2>
          <div className="form-grid">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Woodside Spring HT 2026"
            />
            <label htmlFor="rows">Score sheet rows</label>
            <input
              id="rows"
              type="number"
              min={1}
              value={scoreSheetRows}
              onChange={(e) => setScoreSheetRows(Number(e.target.value) || 20)}
            />
            <label htmlFor="coordinators">Coordinators</label>
            <CoordinatorInput
              id="coordinators"
              value={coordinators}
              onChange={setCoordinators}
              disabled={createMutation.isPending}
            />
          </div>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <button
              className="primary"
              disabled={!name.trim() || createMutation.isPending}
              onClick={() =>
                createMutation.mutate({
                  name: name.trim(),
                  scoreSheetRows,
                  coordinators,
                })
              }
            >
              <CalendarPlus aria-hidden="true" />
              {createMutation.isPending ? 'Creating…' : 'Create event'}
            </button>
            {createMutation.error ? (
              <span className="error">{(createMutation.error as Error).message}</span>
            ) : null}
          </div>
        </div>

        <div className="card events-card">
          <div className="card-heading-row">
            <div>
              <span className="eyebrow">Saved workspaces</span>
              <h2>Events</h2>
            </div>
            <ClipboardList aria-hidden="true" />
          </div>
          {isLoading ? (
            <p className="muted">Loading…</p>
          ) : data && data.events.length > 0 ? (
            <div className="event-card-list">
              {data.events.map((event) => (
                <article key={event.id} className="event-list-card">
                  <Link to={`/events/${event.id}`} className="event-list-card-main">
                    <span className="event-list-card-icon" aria-hidden="true">
                      <ClipboardList />
                    </span>
                    <span>
                      <strong>{event.name}</strong>
                      <small>
                        {event.scoreSheetRows} score-sheet rows · Created{' '}
                        {new Date(event.createdAt).toLocaleDateString()}
                      </small>
                    </span>
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <p className="muted">No events yet. Create one above.</p>
          )}
        </div>
      </div>
    </>
  );
}
